import { type Validation } from "@simplybusiness/mobius";
import type { ReactNode } from "react";
import type { Groups } from "../../../../features/questionnaire";
import type { QuestionValidation } from "../../validation/question/rules/types";
import { type HelpLinkProps } from "./HelpLink";
import type { Display, Options } from "./QuestionFactory";

export type Answer = number | boolean | string | string[];

export interface QuestionProps extends Validation {
  id: string;
  name: string;
  sectionId?: string;
  primaryText: string;
  secondaryText?: string | ReactNode;
  tertiaryText?: string;
  presentationText?: string;
  presentationGroup?: string;
  errorMessage?: string;
  showError?: boolean;
  className?: string;
  helpInfo?: HelpLinkProps;
  placeholder?: string;
  display: Display;
  options?: Options;
  readOnly?: boolean;
  required?: boolean;
  visible?: boolean;
  shouldUpdate?: boolean;
  autocomplete?: string;
  trackAs?: string;
  validation?: QuestionValidation;
  answers?: Record<string, Answer>;
  answer?: Answer;
  hasSensitiveData?: boolean;
  associatedQuestionName?: string;
  includeAnswer?: boolean;
  chatConfig?: ChatConfig;
  loqateMapping?: string;
  hiddenOnAutocomplete?: boolean;
  suggested?: boolean;
  excludeFromPrimaryText?: boolean;
  shouldAnimate?: boolean;
}

export type ChatConfig = {
  text: string;
  chatLabel: string;
  hintText?: string;
};

// The definitions below were added to support question groups
export type QuestionGroupProps = {
  id: string;
  type: string;
  primaryQuestion?: QuestionProps;
  secondaryText?: QuestionProps;
  booleanGroups?: MultiselectGroup[];
  groupQuestions?: (QuestionProps | QuestionGroupProps)[];
};

export type YesNoToggleGroup = QuestionGroupProps & {
  type: "consolidated_question_group";
  primaryQuestion: QuestionProps;
  groupQuestions: QuestionProps[];
};

export type MultiselectGroup = Omit<QuestionGroupProps, "primaryQuestion"> & {
  title: string;
  type: "boolean_group";
  subText?: string;
  groupQuestions: QuestionProps[];
  lastItemDisables?: boolean;
};

export type MultiselectToggleGroup = Omit<
  QuestionGroupProps,
  "groupQuestions"
> & {
  type: "consolidated_boolean_group";
  primaryQuestion: QuestionProps;
  booleanGroups: MultiselectGroup[];
};

export type AddressLookupGroup = Omit<
  QuestionGroupProps,
  "primaryQuestion" | "groupQuestions"
> & {
  title: string;
  type: "address_autocomplete_group";
  groupQuestions: QuestionProps[];
  manualEntryDisabled: boolean;
  subText?: string;
  countryCode?: string;
  apiKey?: string;
};

export type PercentGroup = Omit<
  QuestionGroupProps,
  "primaryQuestion" | "groupQuestions"
> & {
  title: string;
  type: "percent_group";
  groupQuestions: QuestionProps[];
};

export type AgreementType = Omit<
  QuestionGroupProps,
  "primaryQuestion" | "groupQuestions"
> & {
  title: string;
  type: "agreements_question_group";
  groupQuestions: QuestionProps[];
};

export type PersonalisedCoverGroup = QuestionGroupProps & {
  type: "personalised_cover_question_group";
  primaryQuestion: QuestionProps;
  groupQuestions: QuestionProps[];
  mutuallyExclusiveCoverage?: boolean;
};

export type QuestionOrGroup =
  | QuestionProps
  | QuestionGroupProps
  | MultiselectGroup
  | MultiselectToggleGroup
  | AddressLookupGroup
  | PercentGroup
  | PersonalisedCoverGroup;

export type PartialQuestion = Partial<QuestionProps>;
export type PartialQuestionOrGroup = Partial<QuestionOrGroup>;

// helpers
export const getItemType = (item: PartialQuestionOrGroup): string => {
  if ("type" in item && item.type !== undefined) {
    return item.type;
  }
  return "question";
};

export const isQuestion = (
  item: PartialQuestionOrGroup,
): item is QuestionProps => getItemType(item) === "question";

export const isYesNoToggleGroup = (
  group: PartialQuestionOrGroup,
): group is YesNoToggleGroup =>
  getItemType(group) === "consolidated_question_group";

export const isMultiselectGroup = (
  group: PartialQuestionOrGroup,
): group is MultiselectGroup => getItemType(group) === "boolean_group";

export const isMultiselectToggleGroup = (
  group: PartialQuestionOrGroup,
): group is MultiselectToggleGroup =>
  getItemType(group) === "consolidated_boolean_group";

export const isAddressLookupGroup = (
  group: PartialQuestionOrGroup,
): group is AddressLookupGroup =>
  getItemType(group) === "address_autocomplete_group";

export const isPercentGroup = (
  group: PartialQuestionOrGroup,
): group is PercentGroup => getItemType(group) === "percent_group";

export const isAgreementType = (
  group: PartialQuestionOrGroup,
): group is AgreementType => getItemType(group) === "agreements_question_group";

export const isPersonalisedCoverQuestionGroup = (
  group: PartialQuestionOrGroup,
): group is PersonalisedCoverGroup =>
  getItemType(group) === "personalised_cover_question_group";

export const isPersonalisedCoverShadowGroup = (
  group: PartialQuestionOrGroup,
): group is PersonalisedCoverGroup & { mutuallyExclusiveCoverage: true } =>
  isPersonalisedCoverQuestionGroup(group) &&
  "mutuallyExclusiveCoverage" in group &&
  group.mutuallyExclusiveCoverage === true;

export const isQuestionInGroup = (
  questionName: string,
  groups: Groups,
): boolean => {
  return Object.values(groups).some(group =>
    group.names.includes(questionName),
  );
};
